import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

interface HubSpotModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function HubSpotModal({ isOpen, onClose }: HubSpotModalProps) {
  const formRef = useRef<HTMLDivElement>(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isFormLoaded, setIsFormLoaded] = useState(false);

  // Load HubSpot script only when modal is opened
  useEffect(() => {
    if (!isOpen || isScriptLoaded) return;

    const script = document.createElement('script');
    script.src = 'https://js.hsforms.net/forms/embed/v2.js';
    script.async = true;
    script.defer = true;

    script.onload = () => {
      setIsScriptLoaded(true);
    };

    document.body.appendChild(script);

    return () => {
      if (script.parentNode) {
        document.body.removeChild(script);
      }
      setIsScriptLoaded(false);
    };
  }, [isOpen, isScriptLoaded]);

  // Create form once script is loaded
  useEffect(() => {
    if (!isOpen || !isScriptLoaded || !formRef.current || isFormLoaded) return;

    if (window.hbspt) {
      window.hbspt.forms.create({
        region: 'na1',
        portalId: '4621272',
        formId: '359a2885-18f8-415d-adfb-f8e4e9cd36dd',
        target: '#hubspot-form-container',
        css: `
          .hs-form {
            border: 1px solid;
            border-image: linear-gradient(to right, #d03484, #d47254) 1;
            border-radius: 8px;
            padding: 20px;
          }
        `
      });
      setIsFormLoaded(true);
    }
  }, [isOpen, isScriptLoaded, isFormLoaded]);

  // Reset form when modal is closed
  useEffect(() => {
    if (!isOpen && formRef.current) {
      formRef.current.innerHTML = '';
      setIsFormLoaded(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600"
        >
          <X size={24} />
        </button>

        <div className="p-8">
          <div className="flex items-center gap-4 mb-6">
            <img 
              src="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/assets//Recruteurs.io%20Logo%20Final-04.png"
              alt="R"
              className="w-12 h-12 object-contain"
              loading="lazy"
            />
            <h2 className="text-2xl font-bold">
              Rencontrez un consultant
            </h2>
          </div>
          
          <div id="hubspot-form-container" ref={formRef}>
            {!isFormLoaded && (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}